import { useState, useMemo, useEffect, useReducer } from 'react';

import { Box, SelectChangeEvent, Stack } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useHistory } from 'react-router-dom';

import { ApplicationAccessType, CommunicationType, WhatsAppTemplateType } from 'domains/content/types';
import ContentNameInput from 'domains/core/components/ContentNameInput';
import LanguageTabBar from 'domains/core/components/LanguageTabBar';
import PageLoader from 'domains/core/components/PageLoader';
import Select from 'domains/core/components/Select';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import useCreateWhatsApp from 'hooks/mutations/useCreateWhatsApp';
import useUpdateWhatsApp from 'hooks/mutations/useUpdateWhatsApp';
import useContactColumns from 'hooks/queries/useContactColumns';
import useWhatsApp from 'hooks/queries/useWhatsApp';
import useQueryParams from 'hooks/useQueryParams';
import { URLPaths } from 'models/enums';
import ContentStatusText, { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText';
import { getMergeTags } from 'utils';

import { WhatsAppContentWrapperLegacy, WhatsAppContentWrapperFull } from './components/WhatsAppContentWrapper';
import WhatsAppHeaderSection from './components/WhatsAppHeaderSection';
import useFormatWhatsAppContent from './hooks/useFormatWhatsAppContent';
import { getBodyComponentText } from './utils/whatsAppUtils';
import {
    setName,
    setBodyText,
    setApplicationsAccess,
    setCommunicationType,
    setWhatsAppState,
    setHeader,
    setFooter,
    setLanguage,
    setTemplateType,
    addButton,
    updateButton,
    removeButton,
    reorderButtons,
    addCarouselCard,
    updateCarouselCard,
    removeCarouselCard,
    setLTO,
    setSelectedLanguage,
    addLanguage,
    deleteLanguage,
} from './whatsAppActions';
import { whatsAppReducer, whatsAppInitialState, initializeWhatsAppState } from './whatsAppReducer';

const TEMPLATE_TYPE_OPTIONS = [
    { value: WhatsAppTemplateType.STANDARD, label: 'Standard' },
    { value: WhatsAppTemplateType.CAROUSEL, label: 'Carousel' },
];

const ContentWhatsAppBuilderPage = () => {
    const [state, dispatch] = useReducer(whatsAppReducer, whatsAppInitialState);
    const [isNameTouched, setIsNameTouched] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const {
        contentDraft: hasContentDraft,
        newWhatsappBuilder: hasNewBuilder,
        mlTemplateBuilder: isMlEnabled,
    } = useFlags();
    const history = useHistory();

    // URL params
    const query = useQueryParams();
    const whatsappTemplateId = query.get('id');
    const parentFolderId = Number(query.get('parentFolderId'));
    const isCopy = query.get('isCopy') === 'true';
    const statusFromUrl = query.get('status') as ContentStatus | null;

    // Fetch WhatsApp template
    const {
        data: whatsAppData,
        isInitialLoading: isWhatsAppLoading,
        isSuccess: isWhatsAppSuccess,
    } = useWhatsApp(whatsappTemplateId, { enabled: !!whatsappTemplateId && whatsappTemplateId !== 'default' });

    // Fetch contact columns for merge tags
    const { data: contactColumns, isSuccess: isContactColumnsSuccess } = useContactColumns();

    // Initialize state from API data
    useEffect(() => {
        if (!whatsappTemplateId) {
            dispatch(setName('Untitled'));
            dispatch(setApplicationsAccess([ApplicationAccessType.INJOURNEY]));
            dispatch(setCommunicationType(CommunicationType.COMMERCIAL));
        }
    }, [whatsappTemplateId]);

    useEffect(() => {
        if (isWhatsAppSuccess && whatsAppData) {
            const initializedState = initializeWhatsAppState(whatsAppData, isCopy);
            if (statusFromUrl) {
                initializedState.status = statusFromUrl;
            }
            dispatch(setWhatsAppState(initializedState));
        }
    }, [isWhatsAppSuccess, whatsAppData, isCopy, statusFromUrl]);

    // Extract body text for the legacy editor
    const content = useMemo(() => getBodyComponentText({ components: state.components }), [state.components]);

    // Merge tags for preview
    const mergeTags = useMemo(() => {
        if (isContactColumnsSuccess && contactColumns) return getMergeTags(contactColumns);
        return [];
    }, [contactColumns, isContactColumnsSuccess]);

    // Formatted preview content for legacy builder
    const formattedPreviewContent = useFormatWhatsAppContent(content, mergeTags);

    const navigateToContent = () => {
        const contentPath = parentFolderId ? `${URLPaths.CONTENT_FOLDER}/${parentFolderId}` : URLPaths.CONTENT;
        history.push(contentPath);
    };

    const { mutate: createWhatsApp, isLoading: isCreateLoading, error: createError } = useCreateWhatsApp({
        onSuccess: navigateToContent,
    });

    const {
        mutate: updateWhatsApp,
        isLoading: isUpdateLoading,
        error: updateError,
    } = useUpdateWhatsApp(whatsappTemplateId, { onSuccess: navigateToContent });

    const isValidId = whatsappTemplateId && whatsappTemplateId !== 'default' && !isCopy;
    const isUpdate = !!isValidId;

    const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!isNameTouched) setIsNameTouched(true);
        dispatch(setName(e.target.value));
    };

    const handleSave = (status?: ContentStatus) => {
        // Strip the UI-only templateType field before saving — components array is the source of truth
        const { templateType: _omit, ...restState } = state;

        // Build multilingual map: flush the current language's components into the map
        const componentsMultilingual = isMlEnabled
            ? { ...restState.componentsByLanguage, [restState.selectedLanguage]: restState.components }
            : undefined;

        // Backward compat: en-US components as single-language fallback; fall back to current language
        const backwardCompatComponents = isMlEnabled
            ? componentsMultilingual['en-US'] ?? restState.components
            : restState.components;

        const mutateInput = {
            name: restState.name,
            language: restState.language,
            components: backwardCompatComponents,
            ...(isMlEnabled && { componentsMultilingual }),
            communicationType: restState.communicationType,
            applicationsAccess: restState.applicationsAccess.join(','),
            ...(restState.whatsappAccountId && { whatsappAccountId: restState.whatsappAccountId }),
            ...(restState.whatsappNamespace && { whatsappNamespace: restState.whatsappNamespace }),
            ...(restState.whatsappTemplateId && { whatsappTemplateId: restState.whatsappTemplateId }),
            ...(status && { status }),
            ...(parentFolderId && !isUpdate && { parentFolderId }),
        };

        isValidId ? updateWhatsApp({ whatsappTemplateId, ...mutateInput }) : createWhatsApp(mutateInput);
    };

    useEffect(() => {
        if (createError) setErrorMessage(createError.message);
        else if (updateError) setErrorMessage(updateError.message);
    }, [createError, updateError]);

    // Validation
    const isNameUndefinedOnTouch = !state.name && isNameTouched;
    const isNameTooShort = isNameTouched && state.name.length < 3;
    const nameHelperText = isNameUndefinedOnTouch
        ? 'Please enter a WhatsApp name'
        : isNameTooShort
        ? 'WhatsApp name must have at least 3 characters'
        : '';

    const isLoading = isWhatsAppLoading || isCreateLoading || isUpdateLoading;
    const isError = !!errorMessage || !!createError || !!updateError;
    const errorDisplayMessage = errorMessage || createError?.message || updateError?.message;

    // Show loader while fetching an existing template (not on the create/new flow)
    if (isWhatsAppLoading) {
        return <PageLoader />;
    }

    return (
        <Stack height="100%" mb={4} rowGap={4}>
            {isError && <ErrorSnackbar errorMessage={errorDisplayMessage} />}

            <Box sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: 2 }}>
                <ContentNameInput
                    dataTestId="template-name-input"
                    disabled={isLoading}
                    error={isNameUndefinedOnTouch || isNameTooShort}
                    helperText={nameHelperText}
                    name={state.name}
                    onChange={handleChangeName}
                    withClickAway
                />
                {hasContentDraft && (
                    <Box sx={{ pt: 3 }}>
                        <ContentStatusText
                            status={
                                whatsappTemplateId && whatsappTemplateId !== 'default' && !isCopy
                                    ? state.status || ' '
                                    : ContentStatus.DRAFT
                            }
                        />
                    </Box>
                )}
            </Box>

            {/* Template type selector — only visible in new builder */}
            {hasNewBuilder && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Select
                        data-testid="template-type-select"
                        label="Template type"
                        value={state.templateType}
                        onChange={(e: SelectChangeEvent<unknown>) =>
                            dispatch(setTemplateType(e.target.value as WhatsAppTemplateType))
                        }
                        size="small"
                        fullWidth={false}
                        sx={{ minWidth: 220 }}
                        values={TEMPLATE_TYPE_OPTIONS}
                        inputProps={{ 'aria-label': 'Template type' }}
                    />
                </Box>
            )}

            {isMlEnabled && (
                <LanguageTabBar
                    languages={state.languages}
                    selectedLanguage={state.selectedLanguage}
                    onSelectLanguage={(lang) => dispatch(setSelectedLanguage(lang))}
                    onAddLanguage={(lang) => dispatch(addLanguage(lang))}
                    onDeleteLanguage={(lang) => dispatch(deleteLanguage(lang))}
                    disabled={isLoading}
                />
            )}

            <WhatsAppHeaderSection isLoading={isLoading} onSaveAndExit={handleSave} content={content} />

            {hasNewBuilder ? (
                <WhatsAppContentWrapperFull
                    state={state}
                    mergeTags={mergeTags}
                    onSetBodyText={(text) => dispatch(setBodyText(text))}
                    onSetHeader={(h) => dispatch(setHeader(h))}
                    onSetFooter={(f) => dispatch(setFooter(f))}
                    onSetLanguage={(lang) => dispatch(setLanguage(lang))}
                    onAddButton={(btn) => dispatch(addButton(btn))}
                    onUpdateButton={(i, btn) => dispatch(updateButton(i, btn))}
                    onRemoveButton={(i) => dispatch(removeButton(i))}
                    onReorderButtons={(btns) => dispatch(reorderButtons(btns))}
                    onAddCarouselCard={(card) => dispatch(addCarouselCard(card))}
                    onUpdateCarouselCard={(i, card) => dispatch(updateCarouselCard(i, card))}
                    onRemoveCarouselCard={(i) => dispatch(removeCarouselCard(i))}
                    onSetLTO={(lto) => dispatch(setLTO(lto))}
                />
            ) : (
                <WhatsAppContentWrapperLegacy
                    content={content}
                    formattedPreviewContent={formattedPreviewContent}
                    setContent={(text) => dispatch(setBodyText(text))}
                />
            )}
        </Stack>
    );
};

export default ContentWhatsAppBuilderPage;
